const primitiveColors = {
  white: "#ffffff",
  black: "#000000",
  transparent: "transparent",

  // Grays
  grayCold: {
    50: "#F5F6F7",
    100: "#EEF0F2",
    200: "#e8eaec",
    300: "#dee0e4",
    400: "#d7dade",
    500: "#cdd1d6", // Base
    600: "#bbbec3",
    700: "#929498",
    800: "#666977",
    900: "#24283D",
  },
  grayWarm: {
    50: "#FAFAFA",
    100: "#f7f6f4",
    200: "#f3f2ef",
    300: "#eeece8",
    400: "#eae8e4",
    500: "#e5e2dd", // Base
    600: "#d0cec9",
    700: "#a3a09d",
    800: "#7e7c7a",
    900: "#605f5d",
  },

  // Blues (Primary)
  blue: {
    50: "#EEF7FF",
    100: "#C7DEFF",
    200: "#a3c4fa",
    300: "#7aa9f8",
    400: "#6099f6",
    500: "#387ff4", // Base
    600: "#3374de",
    700: "#285aad",
    800: "#1f4686",
    900: "#183566",
  },

  // Pinks
  pink: {
    50: "#fdebf3",
    100: "#f8d6e4",
    200: "#f7a4c7",
    300: "#f47bae",
    400: "#f1619e",
    500: "#ee3a86", // Base
    600: "#d9357a",
    700: "#a9295f",
    800: "#83204a",
    900: "#641838",
  },

  // Greens
  green: {
    50: "#ebf8f3",
    100: "#c0ead9",
    200: "#a1e0c6",
    300: "#76d2ac",
    400: "#5cc99c",
    500: "#33bc83", // Base
    600: "#2eab77",
    700: "#24855d",
    800: "#1c6748",
    900: "#154f37",
  },

  // Purples
  purple: {
    50: "#efeaf4",
    100: "#cebedd",
    200: "#b69ecc",
    300: "#9472b5",
    400: "#8057a7",
    500: "#602d91", // Base
    600: "#572984",
    700: "#442067",
    800: "#351950",
    900: "#28133d",
  },

  // Reds (Danger/Error)
  red: {
    // Note: Shades 50-400 were previously from pink in the 'danger' alias, keeping red for consistency
    50: "#fef2f2", // Example light red
    100: "#fee2e2", // Example light red
    200: "#fecaca", // Example light red
    300: "#fca5a5", // Example light red
    400: "#f87171", // Example light red
    500: "#ED1C24", // Base (Original Red 500)
    600: "#D9357A", // Original Red 600
    700: "#A9295F", // Original Red 700
    800: "#83204A", // Original Red 800
    900: "#641838", // Original Red 900
  },
  // Yellows (Warning) - Added based on common needs
  yellow: {
    50: "#fefce8",
    100: "#fef9c3",
    200: "#fef08a",
    300: "#fde047",
    400: "#facc15",
    500: "#eab308", // Base
    600: "#ca8a04",
    700: "#a16207",
    800: "#854d0e",
    900: "#713f12",
  },
  // Oranges - Added based on common needs & tubeMap
  orange: {
    50: "#fff7ed",
    100: "#ffedd5",
    200: "#fed7aa",
    300: "#fdba74",
    400: "#fb923c",
    500: "#f97316", // Base
    600: "#ea580c",
    700: "#c2410c",
    800: "#9a3412",
    900: "#7c2d12",
  },
  // Cyan/Ocean - Added based on tubeMap
  cyan: {
    50: "#ecfeff",
    100: "#cffafe",
    200: "#a5f3fc",
    300: "#67e8f9",
    400: "#22d3ee",
    500: "#06b6d4", // Base
    600: "#0891b2",
    700: "#0e7490",
    800: "#155e75",
    900: "#164e63",
  },
};

const semanticColors = {
  // Backgrounds
  background: {
    canvas: primitiveColors.grayCold[50],
    default: primitiveColors.white,
    subtle: primitiveColors.grayCold[100],
    disabled: primitiveColors.grayCold[200],
    primary: primitiveColors.blue[500],
    primarySubtle: primitiveColors.blue[50],
    danger: primitiveColors.red[500],
    dangerSubtle: primitiveColors.red[50],
    success: primitiveColors.green[500],
    successSubtle: primitiveColors.green[50],
    warning: primitiveColors.yellow[500],
    warningSubtle: primitiveColors.yellow[50],
  },
  // Text / Foreground Content
  text: {
    default: primitiveColors.grayCold[900],
    subtle: primitiveColors.grayCold[700],
    placeholder: primitiveColors.grayCold[500],
    disabled: primitiveColors.grayCold[400],
    primary: primitiveColors.blue[700],
    primaryOnPrimary: primitiveColors.white,
    danger: primitiveColors.red[700],
    dangerOnDanger: primitiveColors.white,
    success: primitiveColors.green[700],
    successOnSuccess: primitiveColors.white,
    warning: primitiveColors.yellow[700],
    warningOnWarning: primitiveColors.grayCold[900],
    inverted: primitiveColors.white,
  },
  // Borders / Dividers
  border: {
    default: primitiveColors.grayCold[400],
    subtle: primitiveColors.grayCold[200],
    strong: primitiveColors.grayCold[500],
    disabled: primitiveColors.grayCold[300],
    primary: primitiveColors.blue[500],
    danger: primitiveColors.red[500],
    success: primitiveColors.green[500],
    warning: primitiveColors.yellow[500],
    focus: primitiveColors.blue[400],
  },
  // Interactive Elements (Buttons, Links)
  interactive: {
    // Primary (Blue)
    primaryDefault: primitiveColors.blue[500],
    primaryHover: primitiveColors.blue[600],
    primaryActive: primitiveColors.blue[700],
    primaryDisabled: primitiveColors.grayCold[300],
    primarySubtleDefault: primitiveColors.blue[50],
    primarySubtleHover: primitiveColors.blue[100],
    // Danger (Red)
    dangerDefault: primitiveColors.red[500],
    dangerHover: primitiveColors.red[600],
    dangerActive: primitiveColors.red[700],
    dangerDisabled: primitiveColors.grayCold[300],
    dangerSubtleDefault: primitiveColors.red[50],
    dangerSubtleHover: primitiveColors.red[100],
    // Others (e.g., Secondary/Gray button)
    neutralDefault: primitiveColors.white,
    neutralHover: primitiveColors.grayCold[100],
    neutralActive: primitiveColors.grayCold[200],
    neutralDisabled: primitiveColors.grayCold[300],
  },
  // Icons
  icon: {
    default: primitiveColors.grayCold[700],
    subtle: primitiveColors.grayCold[500],
    disabled: primitiveColors.grayCold[400],
    primary: primitiveColors.blue[500],
    danger: primitiveColors.red[500],
    success: primitiveColors.green[500],
    warning: primitiveColors.yellow[500],
    inverted: primitiveColors.white,
  },
  // Focus Rings
  focus: {
    default: primitiveColors.blue[400],
  },
};

const specializedColors = {
  tubeMap: {
    blue: "#4A90E2",
    pink: "#EE3A86",
    yellow: "#F5BC00",
    green: "#33BC83",
    violet: "#602D91",
    orange: "#FF7300",
    ocean: "#00958B",
    red: "#ED1C24",
    black: "#000000",
    sky: "#00B7F5",
    leaf: "#7AB422",
    night: "#2A2A8F",
    gray: "#cdd1d6",
  },
  tubeMapPale: {
    blue: "#A8D0FF",
    pink: "#FAC2D9",
    yellow: "#FFEBB5",
    green: "#96E0C0",
    violet: "#CFB6E7",
    orange: "#FCBF8D",
    ocean: "#98F2E4",
    red: "#F9B9BB",
    black: "#BCBCBC",
    sky: "#CCF0FF",
    leaf: "#CBE58F",
    night: "#AAADDF",
    gray: "#F0F2F4",
  },
  labware: {
    blue: "#A8D0FF",
    pink: "#FAC2D9",
    yellow: "#ffdf8f",
    green: "#96e0c0",
    violet: "#c4a8e5",
    orange: "#f9b27d",
    ocean: "#52ccb5",
    red: "#f48491",
    sky: "#94e3ff",
    leaf: "#cbe58f",
    night: "#aaaddf",
    gray: "#bbbec3",
    peach: "#ffd3b6",
    lavender: "#d3c7ff",
    mint: "#93e5dd",
    salmon: "#ffbbbb",
    azure: "#8ab7ff",
    sand: "#efca89",
    rose: "#ff99c3",
  },
  sample: {
    blue: "#3674BA",
    blueLight: "#A8C6E6",
    pink: "#EE3A86",
    pinkLight: "#F7B4D4",
    yellow: "#F5BC00",
    yellowLight: "#FFE899",
    green: "#33BC83",
    greenLight: "#94D6B6",
    violet: "#682D91",
    violetLight: "#C4A6E6",
    orange: "#FF7300",
    orangeLight: "#FFBB80",
    ocean: "#00958B",
    oceanLight: "#A8E6E0",
    red: "#ED1C24",
    redLight: "#FF9999",
    black: "#000000",
    blackLight: "#8C8C8C",
    sky: "#00B7F5",
    skyLight: "#B8E6FA",
    night: "#1A2A8F",
    nightLight: "#9999D6",
    leaf: "#7AB422",
    leafLight: "#B8E680",
  },
};

const typography = {
  fonts: {
    sans: '"Epilogue", ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
    serif: 'ui-serif, Georgia, Cambria, "Times New Roman", Times, serif',
    mono: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
  },
  fontSizes: {
    "xs": "0.75rem", // 12px
    "sm": "0.875rem", // 14px
    "base": "1rem", // 16px
    "lg": "1.125rem", // 18px
    "xl": "1.25rem", // 20px
    "2xl": "1.5rem", // 24px
    "3xl": "1.875rem", // 30px
    "4xl": "2.25rem", // 36px
    "5xl": "3rem", // 48px
    "6xl": "3.75rem", // 60px
    "7xl": "4.5rem", // 72px
    "8xl": "6rem", // 96px
    "9xl": "8rem", // 128px
  },
  fontWeights: {
    thin: 100,
    extralight: 200,
    light: 300,
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800,
    black: 900,
  },
  lineHeights: {
    "none": "1",
    "tight": "1.25",
    "snug": "1.375",
    "normal": "1.5",
    "relaxed": "1.625",
    "loose": "2",
    // Numeric values matching font sizes for specific use cases
    "xs": "1rem",
    "sm": "1.25rem",
    "base": "1.5rem",
    "lg": "1.75rem",
    "xl": "1.75rem",
    "2xl": "2rem",
    "3xl": "2.25rem",
    "4xl": "2.5rem",
    "5xl": "1", // Often reset for large headings
    "6xl": "1",
    "7xl": "1",
    "8xl": "1",
    "9xl": "1",
  },
  // Add letter spacing, text decoration etc. if needed
};

const spacing = {
  "px": "1px",
  "0": "0",
  "0.5": "0.125rem", // 2px
  "1": "0.25rem", // 4px
  "1.5": "0.375rem", // 6px
  "2": "0.5rem", // 8px
  "2.5": "0.625rem", // 10px
  "3": "0.75rem", // 12px
  "3.5": "0.875rem", // 14px
  "4": "1rem", // 16px
  "5": "1.25rem", // 20px
  "6": "1.5rem", // 24px
  "7": "1.75rem", // 28px
  "8": "2rem", // 32px
  "9": "2.25rem", // 36px
  "10": "2.5rem", // 40px
  "11": "2.75rem", // 44px
  "12": "3rem", // 48px
  "14": "3.5rem", // 56px
  "16": "4rem", // 64px
  "20": "5rem", // 80px
  "24": "6rem", // 96px
  "28": "7rem", // 112px
  "32": "8rem", // 128px
  "36": "9rem", // 144px
  "40": "10rem", // 160px
  "44": "11rem", // 176px
  "48": "12rem", // 192px
  "52": "13rem", // 208px
  "56": "14rem", // 224px
  "60": "15rem", // 240px
  "64": "16rem", // 256px
  "72": "18rem", // 288px
  "80": "20rem", // 320px
  "96": "24rem", // 384px
};

const radii = {
  "none": "0",
  "sm": "0.125rem", // 2px
  "DEFAULT": "0.25rem", // 4px (Tailwind's default rounded)
  "md": "0.375rem", // 6px
  "lg": "0.5rem", // 8px (Tailwind's rounded-lg)
  "xl": "0.75rem", // 12px
  "2xl": "1rem", // 16px
  "3xl": "1.5rem", // 24px
  "full": "9999px",
};

export const theme = {
  primitive: primitiveColors,
  semantic: semanticColors,
  specialized: specializedColors,
  typography: typography,
  spacing: spacing,
  radii: radii,
};

export type Theme = typeof theme;
